// CHUNK 12: Admin synastry metrics dashboard at /admin-ap/sinastria-metricas
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface MetricsRow {
  week:                      string;
  total_synastries:          number;
  invite_synastries:         number;
  manual_synastries:         number;
  completed_invites:         number;
  invite_completion_rate_pct: number | null;
  total_shares:              number;
  share_rate_pct:            number | null;
  signups_from_synastry:     number;
  signup_conversion_rate_pct: number | null;
  viral_coefficient:         number | null;
}

function Kpi({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="border border-[#C9A84C]/20 p-5 rounded-sm bg-[#0D1130]/40">
      <div className="text-[#F5ECD7]/50 text-xs uppercase tracking-widest mb-1">{label}</div>
      <div className="text-[#C9A84C] text-3xl font-light">{value}</div>
      {sub && <div className="text-[#F5ECD7]/40 text-xs mt-1">{sub}</div>}
    </div>
  );
}

export function SynastryMetricsPage() {
  const [rows,    setRows]    = useState<MetricsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    supabase
      .from('synastry_metrics')
      .select('*')
      .order('week', { ascending: false })
      .limit(12)
      .then(({ data, error: e }) => {
        if (e) { setError(e.message); }
        else    { setRows((data ?? []) as MetricsRow[]); }
        setLoading(false);
      });
  }, []);

  const latest = rows[0];
  const fmt = (n: number | null) => n == null ? '-' : `${n}%`;

  return (
    <div className="p-8 text-[#F5ECD7]">
      <div className="mb-8">
        <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider mb-1">
          Métricas de Sinastria
        </h1>
        <p className="text-[#F5ECD7]/50 text-sm">Viral loops, invite completion e conversão</p>
      </div>

      {loading && <div className="text-[#F5ECD7]/50 text-sm">Carregando…</div>}
      {error   && <div className="mb-4 p-4 border border-red-500/40 bg-red-900/20 text-red-300 text-sm rounded-sm">{error}</div>}

      {!loading && !error && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <Kpi label="Sinastrias (semana)" value={latest?.total_synastries ?? 0} sub={`${latest?.invite_synastries ?? 0} via invite`} />
            <Kpi label="Invite completion" value={fmt(latest?.invite_completion_rate_pct ?? null)} sub={`${latest?.completed_invites ?? 0} completados`} />
            <Kpi label="Share rate" value={fmt(latest?.share_rate_pct ?? null)} sub={`${latest?.total_shares ?? 0} total`} />
            <Kpi
              label="Coeficiente viral"
              value={latest?.viral_coefficient?.toFixed(2) ?? '-'}
              sub={(latest?.viral_coefficient ?? 0) >= 0.4 ? '✅ Sustentável' : '⚠️ Abaixo do ideal'}
            />
          </div>

          {/* Conversion funnel */}
          {latest && (
            <div className="mb-10 border border-[#C9A84C]/20 rounded-sm p-6">
              <h2 className="text-[#F5ECD7]/70 text-xs uppercase tracking-widest mb-5">Funil de conversão (invite links)</h2>
              <div className="space-y-4">
                {[
                  { label: 'Invites criados',       value: latest.invite_synastries,         pct: 100 },
                  { label: 'Invites completados',   value: latest.completed_invites,         pct: latest.invite_completion_rate_pct ?? 0 },
                  { label: 'Conversão em cadastro', value: latest.signups_from_synastry,     pct: latest.signup_conversion_rate_pct ?? 0 },
                ].map(step => (
                  <div key={step.label}>
                    <div className="flex justify-between mb-1">
                      <span className="text-[#F5ECD7]/70 text-sm">{step.label}</span>
                      <span className="text-[#F5ECD7]/50 text-sm">{step.value} ({step.pct.toFixed(0)}%)</span>
                    </div>
                    <div className="h-1.5 bg-[#C9A84C]/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#C9A84C] rounded-full transition-all"
                        style={{ width: `${step.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Weekly table */}
          <div>
            <h2 className="text-[#F5ECD7]/70 text-xs uppercase tracking-widest mb-4">Histórico semanal</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[#C9A84C]/20">
                    {['Semana', 'Total', 'Invites', 'Completion', 'Shares', 'Coef. Viral'].map(h => (
                      <th key={h} className="pb-3 pr-6 text-[#F5ECD7]/40 text-xs uppercase tracking-wider font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.week} className="border-b border-[#C9A84C]/10">
                      <td className="py-3 pr-6 text-[#F5ECD7]/60 text-xs">{new Date(r.week).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{r.total_synastries}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{r.invite_synastries}</td>
                      <td className="py-3 pr-6 text-[#C9A84C]">{fmt(r.invite_completion_rate_pct)}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{r.total_shares}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/80">{r.viral_coefficient?.toFixed(2) ?? '-'}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-[#F5ECD7]/30 text-center">Sem dados ainda</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
